import { ICookiePreferences } from '../../Orchestrator/Types/Interface/ICookiePreferences';
import { initAnalytics } from '../Analytics/firebase';
import { initGtm } from '../Analytics/gtm';

const COOKIE_CONSENT_KEY = 'etihad_rail_cookie_consent';
const COOKIE_PREFERENCES_KEY = 'etihad_rail_cookie_preferences';
const COOKIE_CONSENT_VERSION = '1.0';
const CONSENT_EXPIRY_DAYS = 365;

// Check if user has given consent
export const hasCookieConsent = (): boolean => {
    try {
        const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
        if (!consent) return false;

        const consentData = JSON.parse(consent);
        const now = new Date().getTime();

        // Check if consent has expired
        if (consentData.expiry && now > consentData.expiry) {
            clearCookieConsent();
            return false;
        }

        return consentData.given === true && consentData.version === COOKIE_CONSENT_VERSION;
    } catch (error) {
        console.error('Error checking cookie consent:', error);
        return false;
    }
};

// Save cookie consent
export const saveCookieConsent = (preferences: ICookiePreferences): void => {
    try {
        const now = new Date().getTime();
        const expiry = now + (CONSENT_EXPIRY_DAYS * 24 * 60 * 60 * 1000);

        const consentData = {
            given: true,
            version: COOKIE_CONSENT_VERSION,
            timestamp: now,
            expiry: expiry,
        };

        localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(consentData));
        localStorage.setItem(COOKIE_PREFERENCES_KEY, JSON.stringify(preferences));

        // Set actual cookies based on preferences
        setCookiesBasedOnPreferences(preferences, expiry);

        console.log('Cookie consent saved:', preferences);
    } catch (error) {
        console.error('Error saving cookie consent:', error);
    }
};

// Get cookie preferences
export const getCookiePreferences = (): ICookiePreferences => {
    try {
        const preferences = localStorage.getItem(COOKIE_PREFERENCES_KEY);
        if (!preferences) {
            return getDefaultPreferences();
        }

        return JSON.parse(preferences);
    } catch (error) {
        console.error('Error getting cookie preferences:', error);
        return getDefaultPreferences();
    }
};

// Get default preferences (only necessary cookies enabled)
export const getDefaultPreferences = (): ICookiePreferences => {
    return {
        necessary: true,
        statistic: false,
        preferences: false,
        marketing: false,
    };
};

// Clear cookie consent
export const clearCookieConsent = (): void => {
    try {
        localStorage.removeItem(COOKIE_CONSENT_KEY);
        localStorage.removeItem(COOKIE_PREFERENCES_KEY);

        // Clear non-necessary cookies
        clearNonNecessaryCookies();

        console.log('Cookie consent cleared');
    } catch (error) {
        console.error('Error clearing cookie consent:', error);
    }
};

// Set cookies based on user preferences
const setCookiesBasedOnPreferences = (preferences: ICookiePreferences, expiry: number): void => {
    const expiryDate = new Date(expiry);

    // Always allow necessary cookies
    if (preferences.necessary) {
        // These are already set by the application
        // LOCALE cookie, site_auth, etc.
    }

    // Load the GTM container only once a category that justifies it is granted.
    // The container is NOT in the document head because it carries third-party
    // pixels (Meta/TikTok/Snap) that Google Consent Mode does not gate — see
    // Lib/Analytics/gtm.ts. The consent updates issued below are queued on
    // dataLayer and replay to the container in order once it loads.
    if (preferences.statistic || preferences.marketing) {
        initGtm();
    }

    // Handle statistic cookies (analytics)
    if (preferences.statistic) {
        document.cookie = `etihad_analytics_enabled=true; path=/; expires=${expiryDate.toUTCString()}; SameSite=Lax`;
        // Enable Google Analytics, tracking pixels, etc.
        enableAnalyticsCookies();
    } else {
        document.cookie = `etihad_analytics_enabled=false; path=/; expires=${expiryDate.toUTCString()}; SameSite=Lax`;
        disableAnalyticsCookies();
    }

    // Handle preference cookies
    if (preferences.preferences) {
        document.cookie = `etihad_preferences_enabled=true; path=/; expires=${expiryDate.toUTCString()}; SameSite=Lax`;
        // These can include theme preferences, language settings, etc.
    } else {
        document.cookie = `etihad_preferences_enabled=false; path=/; expires=${expiryDate.toUTCString()}; SameSite=Lax`;
    }

    // Handle marketing cookies
    if (preferences.marketing) {
        document.cookie = `etihad_marketing_enabled=true; path=/; expires=${expiryDate.toUTCString()}; SameSite=Lax`;
        // Enable advertising cookies, retargeting pixels, etc.
        enableMarketingCookies();
    } else {
        document.cookie = `etihad_marketing_enabled=false; path=/; expires=${expiryDate.toUTCString()}; SameSite=Lax`;
        disableMarketingCookies();
    }
};

// Enable analytics cookies (Google Analytics, etc.)
const enableAnalyticsCookies = (): void => {
    // Boot Firebase Analytics (idempotent; respects the runtime flag).
    // This also injects gtag.js so the consent update below takes effect.
    void initAnalytics();

    // Enable Google Analytics tracking
    if (typeof window !== 'undefined' && (window as any).gtag) {
        (window as any).gtag('consent', 'update', {
            analytics_storage: 'granted'
        });
    }

    // Enable other analytics tools
    console.log('Analytics cookies enabled');
};

// Disable analytics cookies
const disableAnalyticsCookies = (): void => {
    if (typeof window !== 'undefined' && (window as any).gtag) {
        (window as any).gtag('consent', 'update', {
            analytics_storage: 'denied'
        });
    }

    console.log('Analytics cookies disabled');
};

// Enable marketing cookies
const enableMarketingCookies = (): void => {
    if (typeof window !== 'undefined' && (window as any).gtag) {
        (window as any).gtag('consent', 'update', {
            ad_storage: 'granted',
            ad_user_data: 'granted',
            ad_personalization: 'granted'
        });
    }

    console.log('Marketing cookies enabled');
};

// Disable marketing cookies
const disableMarketingCookies = (): void => {
    if (typeof window !== 'undefined' && (window as any).gtag) {
        (window as any).gtag('consent', 'update', {
            ad_storage: 'denied',
            ad_user_data: 'denied',
            ad_personalization: 'denied'
        });
    }

    console.log('Marketing cookies disabled');
};

// Clear non-necessary cookies
const clearNonNecessaryCookies = (): void => {
    const cookiesToClear = [
        'etihad_analytics_enabled',
        'etihad_preferences_enabled',
        'etihad_marketing_enabled',
        '_ga',
        '_ga_*',
        '_gid',
        '_gat',
        '_utm_*',
        'fbp',
        '_fbp',
        'fr'
    ];

    cookiesToClear.forEach(cookieName => {
        if (cookieName.includes('*')) {
            // Handle wildcard cookies
            const baseName = cookieName.replace('*', '');
            const allCookies = document.cookie.split(';');
            allCookies.forEach(cookie => {
                const name = cookie.trim().split('=')[0];
                if (name.startsWith(baseName)) {
                    document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 UTC; SameSite=Lax`;
                }
            });
        } else {
            document.cookie = `${cookieName}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 UTC; SameSite=Lax`;
        }
    });
};

// Check if user is first time visitor
export const isFirstTimeVisitor = (): boolean => {
    try {
        return !localStorage.getItem(COOKIE_CONSENT_KEY);
    } catch (error) {
        console.error('Error checking first time visitor:', error);
        return true;
    }
};

// Update preferences without changing consent status
export const updateCookiePreferences = (preferences: ICookiePreferences): void => {
    try {
        localStorage.setItem(COOKIE_PREFERENCES_KEY, JSON.stringify(preferences));

        // Update actual cookies
        const now = new Date().getTime();
        const expiry = now + (CONSENT_EXPIRY_DAYS * 24 * 60 * 60 * 1000);
        setCookiesBasedOnPreferences(preferences, expiry);

        console.log('Cookie preferences updated:', preferences);
    } catch (error) {
        console.error('Error updating cookie preferences:', error);
    }
}; 